import { useCustomCss } from '../lib/customCss';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Laptop,
  LayoutDashboard,
  NotebookPen,
  Smartphone,
  Sparkles,
  UserPlus,
  Users,
} from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { useSiteTheme } from '../lib/palette';
import { rememberReferral } from '../lib/referral';
import { PartnerBanner, usePartnerOffer } from '../ui/PartnerOffer';
import { Logo } from '../ui/Logo';
import { AmbassadorsPage } from './Ambassadors';
import { PhoneTour, usePhone, type Hotspot } from './PhoneTour';
import { ComparePlans, dollars, largeFamilyLine, type PublicPlan } from './ComparePlans';
import { useAuthOptions } from './AuthPage';
import type { SupportContact } from './ContactUs';

/** The product tour: real screenshots of the app with a sample family (made by tools/shot/tour.mjs). */
const TOUR: { id: string; label: string; title: string; text: string }[] = [
  {
    id: 'family',
    label: 'Home',
    title: 'Your whole homeschool at a glance',
    text: 'Who’s in school, what’s due today, new messages and your own to-do list, on one page.',
  },
  {
    id: 'live',
    label: 'Live view',
    title: 'Check in from anywhere, openly',
    text: 'See their screen, hear them and talk during school time. Your child sees a banner while you watch, and live view is never recorded.',
  },
  {
    id: 'safety',
    label: 'Safety controls',
    title: 'School time stays school time',
    text: 'Only the learning sites you choose open. Games, social media and video wait until after school, with an alert if they try.',
  },
  {
    id: 'planner',
    label: 'Planner',
    title: 'Plan the month in one calendar',
    text: 'Assignments, lesson plans and your journal side by side. Repeat an assignment every school day or week, and link it to its lesson.',
  },
  {
    id: 'grades',
    label: 'Grades',
    title: 'A grade book that keeps up',
    text: 'Grades flow in from your planner and from Connexus, or add them yourself. Weighted categories, your own grading scale, and report cards by quarter or semester.',
  },
  {
    id: 'attendance',
    label: 'Attendance',
    title: 'Attendance that matches your records',
    text: 'Enter each day’s hours, split by subject if you like, with a daily estimate as a check. Your state’s days, hours and filing deadlines sit right above.',
  },
  {
    id: 'portfolio',
    label: 'Portfolio',
    title: 'A portfolio your evaluator can open',
    text: 'Work samples, test scores and evaluations, one year-end PDF, and a read-only link for your evaluator that you can turn off any time.',
  },
  {
    id: 'reading',
    label: 'Reading log',
    title: 'Reading your child logs, you sign off',
    text: 'The book, pages and minutes for each day. Your child adds entries, you can too, and you sign them off.',
  },
  {
    id: 'desktop',
    label: 'Today’s work',
    title: 'Right on your child’s computer',
    text: 'School Resources sits on their desktop: today’s work, grades, reading and chat, plus help, a break or “I’m done for today” with one tap.',
  },
  {
    id: 'chat',
    label: 'Chat',
    title: 'Stay connected during the school day',
    text: 'Chat with each child, even from another room or away from home.',
  },
  {
    id: 'lessons',
    label: 'Lesson plans & journal',
    title: 'Plan the lesson, then write down how it went',
    text: 'Write a lesson plan once and link it to its assignments, and keep a short journal for each school day beside the planner.',
  },
  {
    id: 'portal',
    label: 'Student Portal',
    title: 'A portal your kids actually use',
    text: 'Your child signs in to see today’s work, turns it in with what they learned, and checks their grades and reading.',
  },
  {
    id: 'sharing',
    label: 'Family sharing',
    title: 'Bring in a co-parent, tutor or grandparent',
    text: 'Invite another adult and choose exactly what they can see and do, child by child.',
  },
  {
    id: 'reportcard',
    label: 'Report cards',
    title: 'Report cards and progress reports, ready to print',
    text: 'By quarter or semester, filled in from the grade book, with written progress reports from your records.',
  },
  {
    id: 'transcript',
    label: 'Transcript',
    title: 'A high school transcript that builds itself',
    text: 'Courses, credits and grades year by year, with GPA, ready to send with a college application.',
  },
  {
    id: 'tests',
    label: 'Tests & evaluations',
    title: 'Standardized tests and evaluations, on record',
    text: 'Record test scores and evaluations with their reports, so they’re with everything else at the end of the year.',
  },
  {
    id: 'whiteboard',
    label: 'Whiteboard',
    title: 'Work a problem out together',
    text: 'A shared whiteboard you both draw on, from another room or away from home.',
  },
  {
    id: 'requirements',
    label: 'Requirements & deadlines',
    title: 'Know where you stand with your state',
    text: 'Days and hours against your state’s requirement, the pace to finish on time, and a reminder before every deadline.',
  },
  {
    id: 'documents',
    label: 'State documents',
    title: 'Your state’s paperwork, filled in',
    text: 'Notices of intent, IHIPs and quarterly reports filled in from what you already record. Turn on state compliance when you want it: only then do we keep your children’s legal names and birth dates.',
  },
  {
    id: 'evaluator',
    label: 'Evaluator view',
    title: 'A read-only page for your evaluator',
    text: 'Days, hours, time by subject, grades, tests and work samples on one page, with no account needed. Turn the link off any time.',
  },
  {
    id: 'sr-grades',
    label: 'Their grades',
    title: 'Their grades, without opening a browser',
    text: 'School Resources shows each subject’s grades and the overall grade, as you enter them.',
  },
  {
    id: 'sr-reading',
    label: 'Reading log',
    title: 'Log reading from the desktop',
    text: 'The book, pages and minutes for each day, added right from their computer for you to sign off.',
  },
  {
    id: 'sr-chat',
    label: 'Chat',
    title: 'Message you without leaving the lesson',
    text: 'Chat with you from the panel beside their schoolwork, from another room or away from home.',
  },
  {
    id: 'sr-schedule',
    label: 'Schedule',
    title: 'Today’s school hours at a glance',
    text: 'When school starts and ends today, breaks, and the week ahead, so they always know what’s next.',
  },
];

/** One part of a demo screen to point out (#808): its title and words, and where it is, as percentages of the image. */

/** The demo grouped like the Features page (#807): each group's screens as its subtabs, in the group's own color. */
const TOUR_GROUPS: { id: string; name: string; steps: string[] }[] = [
  { id: 'plan', name: 'Plan & teach', steps: ['family', 'planner', 'lessons', 'reading', 'portal', 'sharing'] },
  { id: 'records', name: 'Grades & records', steps: ['grades', 'reportcard', 'attendance', 'transcript', 'tests'] },
  { id: 'focus', name: 'Focused school time', steps: ['live', 'safety', 'chat', 'whiteboard'] },
  { id: 'state', name: 'State compliance', steps: ['requirements', 'documents', 'portfolio', 'evaluator'] },
  // School Resources on the child's own computer (#813), the real Windows app on each of its tabs.
  { id: 'computer', name: 'On their computer', steps: ['desktop', 'sr-grades', 'sr-reading', 'sr-chat', 'sr-schedule'] },
];

/**
 * The demo (#807): the four feature groups across the top, the open group's screens as subtabs, then the screen's title
 * and words, then its screenshot in a browser frame. Arrows step through every screen, group by group.
 */
function ProductTour({ bare }: { bare?: boolean }) {
  const steps = TOUR_GROUPS.flatMap((g) => g.steps.map((id) => ({ group: g, step: TOUR.find((t) => t.id === id)! })));
  const [index, setIndex] = useState(0);
  const subtabs = useRef<(HTMLButtonElement | null)[]>([]);
  const { group, step } = steps[index]!;
  // The guided tour's hotspots (#808): measured from the real app when the screens are captured.
  const [hotspots, setHotspots] = useState<Record<string, Hotspot[]>>({});
  const [spot, setSpot] = useState(-1);
  useEffect(() => {
    fetch('/tour/hotspots.json')
      .then((r) => (r.ok ? r.json() : {}))
      .then(setHotspots, () => undefined);
  }, []);
  useEffect(() => setSpot(-1), [index]);
  const spots = hotspots[step.id] ?? [];
  // Next walks the hotspots on this screen, then moves on to the next screen.
  const next = () => (spot < spots.length - 1 ? setSpot(spot + 1) : go(index + 1));
  const back = () => (spot > 0 ? setSpot(spot - 1) : go(index - 1));
  const inGroup = group.steps.indexOf(step.id);
  const go = (i: number) => setIndex((i + steps.length) % steps.length);
  const openGroup = (g: (typeof TOUR_GROUPS)[number]) => go(steps.findIndex((x) => x.group.id === g.id));
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const n = group.steps.length;
    const next = (inGroup + (e.key === 'ArrowRight' ? 1 : -1) + n) % n;
    go(steps.findIndex((x) => x.step.id === group.steps[next]));
    subtabs.current[next]?.focus();
  };
  const phone = usePhone();
  if (phone)
    return (
      <section id="tour" className="landing-section landing-tour">
        {!bare && (
          <>
            <h2 className="landing-section-title">See it in action</h2>
            <p className="muted landing-tour-sub">A homeschool family’s week, with two kids.</p>
          </>
        )}
        <PhoneTour groups={TOUR_GROUPS} steps={steps} index={index} go={go} spots={spots} />
      </section>
    );
  return (
    <section id="tour" className="landing-section landing-tour">
      {!bare && (
        <>
          <h2 className="landing-section-title">See it in action</h2>
          <p className="muted landing-tour-sub">A homeschool family’s week, with two kids.</p>
        </>
      )}
      <div className="landing-tour-groups" role="group" aria-label="Feature groups">
        {TOUR_GROUPS.map((g) => (
          <button key={g.id} type="button" className={g.id === group.id ? 'active' : ''} aria-pressed={g.id === group.id} onClick={() => openGroup(g)}>
            {g.name}
            <small>{g.steps.length} screens</small>
          </button>
        ))}
      </div>
      <div className="landing-tour-tabs" role="tablist" aria-label={group.name} onKeyDown={onKey}>
        {group.steps.map((id, i) => {
          const t = TOUR.find((x) => x.id === id)!;
          return (
            <button
              key={id}
              ref={(el) => {
                subtabs.current[i] = el;
              }}
              role="tab"
              id={`tour-tab-${id}`}
              aria-selected={id === step.id}
              aria-controls="tour-panel"
              tabIndex={id === step.id ? 0 : -1}
              className={id === step.id ? 'active' : ''}
              onClick={() => go(steps.findIndex((x) => x.step.id === id))}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      <div id="tour-panel" role="tabpanel" aria-labelledby={`tour-tab-${step.id}`} className="landing-tour-panel">
        {/* The words above the screen (#807). */}
        <div className="landing-tour-caption">
          <div>
            <h3>{step.title}</h3>
            <p className="muted">{step.text}</p>
          </div>
          <span className="landing-tour-steps">
            {spots.length > 0 && <span className="muted small">{spot >= 0 ? `${spot + 1} of ${spots.length}` : `${spots.length} things to see`}</span>}
            <button type="button" className="btn" onClick={back}>
              <ChevronLeft size={16} /> Back
            </button>
            <button type="button" className="btn primary" onClick={next}>
              Next <ChevronRight size={16} />
            </button>
          </span>
        </div>
        <div className="landing-frame">
          <div className="landing-frame-bar" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <div className="landing-tour-stage">
            <img src={`/tour/${step.id}.jpg`} width={1920} height={1200} alt={`${step.label}: ${step.title}`} loading={index === 0 ? 'eager' : 'lazy'} />
            {/* Click any part of the screen, or its number, to see what it does (#808). Nothing dims the screen. */}
            {spots.map((h, k) => (
              <button
                key={`a${k}`}
                type="button"
                className={`landing-tour-area ${k === spot ? 'on' : ''}`}
                style={{ left: `${h.r[0]}%`, top: `${h.r[1]}%`, width: `${h.r[2]}%`, height: `${h.r[3]}%` }}
                aria-label={h.title}
                onClick={() => setSpot(k)}
              />
            ))}
            {spots.map((h, k) => (
              <button
                key={`n${k}`}
                type="button"
                className={`landing-tour-dot ${k === spot ? 'on' : ''}`}
                style={{ left: `${h.r[0]}%`, top: `${h.r[1]}%` }}
                aria-label={`${k + 1}: ${h.title}`}
                onClick={() => setSpot(k)}
              >
                {k + 1}
              </button>
            ))}
            {spot >= 0 && spots[spot] && (
              <div
                className="landing-tour-tip"
                role="status"
                style={{
                  // Never past the screen's right edge, even on a phone where the callout is most of its width.
                  left: `min(${Math.max(spots[spot]!.r[0], 1)}%, calc(100% - min(300px, 80%) - 6px))`,
                  ...(spots[spot]!.r[1] + spots[spot]!.r[3] > 72 ? { bottom: `${100 - spots[spot]!.r[1] + 2}%` } : { top: `${spots[spot]!.r[1] + spots[spot]!.r[3] + 2}%` }),
                }}
              >
                <span className="landing-tour-tip-n">
                  {spot + 1} of {spots.length}
                </span>
                <strong>{spots[spot]!.title}</strong>
                <span className="muted">{spots[spot]!.text}</span>
              </div>
            )}
          </div>
        </div>
        {/* Warm the cache for the next screen so switching feels instant. */}
        <link rel="prefetch" href={`/tour/${steps[(index + 1) % steps.length]!.step.id}.jpg`} />
      </div>
    </section>
  );
}

/** The problems a homeschooling parent recognizes. */
const PROBLEMS: { title: string; text: string }[] = [
  { title: 'Planners, spreadsheets and sticky notes', text: 'Lessons in one place, grades in another, attendance on the fridge. Something always slips.' },
  { title: '“Just five minutes” of YouTube', text: 'The computer they learn on is also the one with games, videos and chat a click away.' },
  { title: 'Proving it at the end of the year', text: 'Attendance, grades and a reading list, pieced together the night before they’re due.' },
];


/** The promise that answers “is this spyware?” */
const PROMISE: { title: string; text: string }[] = [
  { title: 'Only during school time', text: 'Nothing is monitored outside the hours you set, or during breaks.' },
  { title: 'Open by default', text: 'A banner shows when you watch or talk, unless you turn on Hidden mode or Ghost mode. The camera and mic always show it.' },
  { title: 'Live view isn’t recorded', text: 'No screen or camera recordings. Website history is deleted after 90 days.' },
  { title: 'Your data stays yours', text: 'Download everything, or delete your account and its data, any time.' },
];

/** Who it fits. */
const FITS: { icon: ReactNode; title: string; text: string }[] = [
  { icon: <Users size={20} />, title: 'Every child', text: 'Each child has their own schedule, rules and schoolwork.' },
  { icon: <UserPlus size={20} />, title: 'Co-parents, tutors and grandparents', text: 'Share your family and choose exactly what each adult can see and do.' },
  { icon: <Laptop size={20} />, title: 'Windows, Mac and Chromebook', text: 'An app for your child’s PC or Mac, or the browser extension.' },
  { icon: <Smartphone size={20} />, title: 'Your phone', text: 'Install ClassGuardian on iPhone or Android and get alerts anywhere.' },
  { icon: <NotebookPen size={20} />, title: 'Online schools', text: 'Bring assignments in from your online school’s planner feed.' },
  { icon: <LayoutDashboard size={20} />, title: 'Shared family computers', text: 'Each child picks their name; no passwords to type.' },
];

/** Everything included, for skimmers. */
/** The Features page's full guide (#707): every feature, grouped by what a parent is trying to get done. */
const GUIDE: { id: string; name: string; blurb: string; title: string; intro: string; highlights: string[]; items: { name: string; text: string; feature?: string }[] }[] = [
  {
    id: 'plan',
    name: 'Plan & teach',
    blurb: 'Planner, lesson plans, journal and a Student Portal',
    title: 'Plan the year, teach the day',
    intro: 'One calendar for assignments, lesson plans and your journal, with a portal your kids actually use.',
    highlights: ['Planner & calendar', 'Lesson plans & journal', 'Student Portal', 'Reading log'],
    items: [
      { name: 'Planner & calendar', text: 'Assignments by day, week or month. Repeat one every school day or week.', feature: 'planner' },
      { name: 'Lesson plans', text: 'Write the plan once and link it to its assignments.', feature: 'lesson_plans' },
      { name: 'Homeschool journal', text: 'A note for each school day, right beside the planner.', feature: 'journal' },
      { name: 'School calendar & holidays', text: 'Set the school year, breaks and holidays once; schedules follow them.' },
      { name: 'Reading log', text: 'Your child logs the book, pages and minutes; you sign off.', feature: 'reading_log' },
      { name: 'Student Portal', text: 'Your child sees today’s work, turns it in, and checks grades and reading.', feature: 'student_portal' },
      { name: 'Family sharing & roles', text: 'Invite a co-parent, tutor or grandparent and choose what each can do.' },
    ],
  },
  {
    id: 'records',
    name: 'Grades & records',
    blurb: 'Grade book, report cards, attendance and hours',
    title: 'Records that keep themselves',
    intro: 'Grades, attendance and hours fill in as the school day happens, ready when you need them.',
    highlights: ['Grade book', 'Report & progress cards', 'Attendance & hours', 'CSV export'],
    items: [
      { name: 'Grade book', text: 'Graded work flows in, with weighted categories and your own scale.', feature: 'grade_book' },
      { name: 'Report cards', text: 'By quarter or semester, ready to print.', feature: 'report_cards' },
      { name: 'Progress reports', text: 'Written progress reports filled in from your records.', feature: 'report_cards' },
      { name: 'Attendance', text: 'Each day’s hours, with a daily estimate as a check.', feature: 'attendance' },
      { name: 'Hours by subject', text: 'The day’s hours split by subject, filled in from the planner.', feature: 'attendance' },
      { name: 'Reports & CSV export', text: 'Reports for any stretch of the year, and downloads of your records.', feature: 'reports' },
    ],
  },
  {
    id: 'focus',
    name: 'Focused school time',
    blurb: 'Safe websites, live view and chat',
    title: 'The computer stays a school computer',
    intro: 'During the hours you set, ClassGuardian keeps your child on task, openly, and gives them an easy way to reach you.',
    highlights: ['Safe websites & app blocking', 'Live view & talk', 'Help when they need you', 'Chat & whiteboard'],
    items: [
      { name: 'Safe websites', text: 'During school time, only the learning sites you allow open.', feature: 'computer' },
      { name: 'App & game blocking', text: 'Games, social media and video wait until after school.', feature: 'app_blocking' },
      { name: 'Live view & talk', text: 'See their screen, hear them and talk. They see a banner, and it’s never recorded.', feature: 'live_view' },
      { name: 'Help requests', text: 'Your child asks for help or says they’re finished; you answer from your phone.', feature: 'help' },
      { name: 'Chat & whiteboard', text: 'Chat with each child and work a problem out together on a shared whiteboard.', feature: 'chat' },
      { name: 'Notifications', text: 'Alerts on your phone or computer when a child needs you.' },
    ],
  },
  {
    id: 'state',
    name: 'State compliance',
    blurb: 'Every state and DC, deadlines and filled-in paperwork',
    title: 'Your state’s paperwork, done',
    intro: 'Pick your state and how you homeschool. Every state and Washington, DC is covered.',
    highlights: ['State requirements', 'Deadline reminders', 'Filled-in state documents', 'Year-end portfolio'],
    items: [
      { name: 'State requirements', text: 'Days and hours against your state’s rule, with the pace to finish on time.', feature: 'state_compliance' },
      { name: 'Deadline reminders', text: 'Reminders before each notice, plan and quarterly report is due.', feature: 'state_compliance' },
      { name: 'State documents', text: 'Notices of intent, IHIPs and progress reports filled in for you.', feature: 'state_compliance' },
      { name: 'Work samples & portfolio', text: 'Collect work samples and get one year-end portfolio PDF.', feature: 'portfolio' },
      { name: 'Tests & evaluations', text: 'Record test scores and evaluations with their reports.', feature: 'grade_book' },
      { name: 'Evaluator sharing', text: 'A read-only link for your evaluator that you can turn off any time.' },
    ],
  },
];

const FAQ: { q: string; a: string }[] = [
  {
    q: 'What does it cost?',
    // Filled in from the plans on the page (#710); this is what shows if they can't be loaded.
    a: 'See the plans on the home page. Every plan starts with a free trial.',
  },
  {
    q: 'Is this spyware?',
    a: 'No. ClassGuardian only works during the school hours you set, never during breaks, and your child sees a banner when you watch or talk (unless you turn on Hidden mode; then it’s up to you to tell them). Live view is never recorded, and your child can ask you for help, a break or to finish early.',
  },
  {
    q: 'What devices does it work on?',
    a: 'Your child: a Windows PC or Mac with the ClassGuardian app, or a Chromebook (or any Chrome or Edge browser) with the extension. You: any phone, tablet or computer, and you can install it as an app on iPhone and Android.',
  },
  {
    q: 'How long does setup take?',
    a: 'About ten minutes: add your child, install ClassGuardian on their computer, enter a 6-digit code and set their school hours. A step-by-step guide walks you through it.',
  },
  {
    q: 'I have more than one child. Does that work?',
    a: 'Yes. Each child gets their own schedule, safety controls, planner and grades, and children can share one computer.',
  },
  {
    q: 'Can my spouse, a tutor or a grandparent help?',
    a: 'Yes. Invite them to your family and choose what each can do: everything, schoolwork only, view only, or exactly the areas you pick.',
  },
  {
    q: 'Will it help with my state’s homeschool records?',
    a: 'Yes. Pick your state and how you homeschool, and ClassGuardian tracks your days and hours against it, reminds you before each filing deadline, and fills in notices of intent, instruction plans and progress reports from your records. At the end of the year you get one portfolio PDF with attendance, grades, reading, work samples and test scores. Every state and Washington, DC is covered; each state’s rule links to its source, and it isn’t legal advice, so check what yours needs.',
  },
  {
    q: 'Can my evaluator see my child’s work?',
    a: 'If you want them to. Make a read-only link on the Portfolio tab, choose what it shows and how long it works, and send it. They don’t need an account and can’t change anything. You see each time it’s opened and can turn it off any time.',
  },
  {
    q: 'Is this legal advice?',
    a: 'No. ClassGuardian keeps your records and fills in the paperwork, but homeschool laws change and you’re responsible for what you file. Each state’s rule shows its source and when we last reviewed it; your state’s education department or a homeschool association can confirm the details.',
  },
  {
    q: 'What happens to my data?',
    a: 'It’s yours. Download everything with one click, or delete your account and its data. Website history is deleted automatically after 90 days. Work samples and test reports stay with your records until you delete them. Your children’s legal names and birth dates are kept only if you turn on state compliance, and turning it off deletes them, and nothing is shared with an evaluator unless you make a link.',
  },
];

/** Call, text or email: the support contact from the server (Admin → Server settings → Support). */
function ContactStrip({ contact }: { contact: SupportContact }) {
  const tel = contact.phone.replace(/[^\d+]/g, '');
  return (
    <p className="landing-contact muted">
      Questions first? Talk to a real person:{' '}
      {[
        tel && (
          <a key="call" href={`tel:${tel}`}>
            {contact.phone}
          </a>
        ),
        tel && (
          <a key="text" href={`sms:${tel}`}>
            Text us
          </a>
        ),
        contact.email && (
          <a key="email" href={`mailto:${contact.email}`}>
            {contact.email}
          </a>
        ),
      ]
        .filter(Boolean)
        .flatMap((a, i) => (i ? [<span key={`dot${i}`}> · </span>, a] : [a]))}
    </p>
  );
}

/** The public pages besides Home (#702), in the order of the top menu. */
export type MarketingPage = 'home' | 'features' | 'demo' | 'faq' | 'ambassadors' | 'compare';
/** `menu: false` pages are linked from the footer only. */
const PAGES: { id: Exclude<MarketingPage, 'home'>; path: string; label: string; title: string; menu?: false }[] = [
  { id: 'features', path: '/features', label: 'Features', title: 'Features' },
  // The demo has its own page (#780); Safety folded into the Features page's Focused school time card.
  { id: 'demo', path: '/demo', label: 'Demo', title: 'See it in action' },
  { id: 'faq', path: '/faq', label: 'FAQ', title: 'Questions parents ask' },
  { id: 'compare', path: '/compare-plans', label: 'Compare plans', title: 'Compare the plans', menu: false },
  { id: 'ambassadors', path: '/ambassadors', label: 'Brand Ambassadors', title: 'Brand Ambassadors', menu: false },
];

/** The public page at a path, or null when the path isn't one. */
export function marketingPageFor(pathname: string): MarketingPage | null {
  if (pathname === '/') return 'home';
  return PAGES.find((p) => p.path === pathname)?.id ?? null;
}


/** "30-day free trial" when every plan has the same trial, "Free trial" when they differ, '' when none has one. */
function trialText(plans: PublicPlan[]) {
  const days = plans.map((p) => p.trialDays);
  if (days.length && days.every((d) => d > 0 && d === days[0])) return `${days[0]}-day free trial`;
  return days.some((d) => d > 0) ? 'Free trial' : '';
}

/** The plans, with the recommended one in the middle (first on a phone), raised and with the main button (#710). */
function PlansSection({ plans, largeFamily, open }: { plans: PublicPlan[]; largeFamily: boolean; open: boolean }) {
  if (!plans.length) return null;
  const rec = plans.find((p) => p.recommended);
  const others = plans.filter((p) => p !== rec);
  const ordered = rec ? [...others.slice(0, 1), rec, ...others.slice(1)] : plans;
  const trial = trialText(plans);
  return (
    <section id="plans" className="landing-section">
      <h2 className="landing-section-title">Plans for every homeschool</h2>
      {/* A trial starts without a card; checkout only comes when it ends (#793). */}
      {trial && (
        <p className="muted landing-tour-sub">
          {trial === 'Free trial' ? 'Start with a free trial.' : `Every plan starts with a ${trial}.`} <strong>No credit card required.</strong> Cancel any
          time.
        </p>
      )}
      <Link to="/compare-plans" className="landing-compare-link">
        Compare plans side by side <ChevronRight size={16} />
      </Link>
      <div className="landing-plans">
        {ordered.map((p) => (
          <div key={p.id} className={`landing-plan-wrap ${p.recommended ? 'recommended' : ''}`}>
            <article className="card landing-plan">
              {p.recommended && <span className="landing-plan-badge">Recommended</span>}
              <h3>{p.name}</h3>
              <p className="landing-plan-price">
                <strong>{dollars(p.monthlyCents)}</strong> <span className="muted">/ month</span>
              </p>
              <p className="muted small landing-plan-terms">
                {[p.yearlyCents > 0 && `or ${dollars(p.yearlyCents)} a year`, p.trialDays > 0 && `${p.trialDays}-day free trial`].filter(Boolean).join(' · ')}
              </p>
              {p.description && <p className="muted">{p.description}</p>}
              <Link className={`btn ${p.recommended ? 'primary big' : ''}`} to="/signup">
                {!open ? 'Request access' : p.recommended && p.trialDays > 0 ? 'Start your free trial' : 'Get started'}
              </Link>
            </article>
          </div>
        ))}
      </div>
      {largeFamily && <p className="muted landing-tour-sub">{largeFamilyLine(plans)}</p>}
    </section>
  );
}

/**
 * Which plans have a feature (#781, #785), from the live plans: "All plans" when every plan has it (or it isn't a plan
 * feature at all, like the planner), "<cheapest> and up" when every dearer plan has it too, or the plans by name.
 * Null when no plan has it.
 */
function planTag(feature: string | undefined, plans: PublicPlan[], planFeatures: string[]): { text: string; all: boolean } | null {
  if (!plans.length) return null;
  if (!feature || !planFeatures.includes(feature)) return { text: 'All plans', all: true };
  const sorted = [...plans].sort((a, b) => a.monthlyCents - b.monthlyCents);
  const has = sorted.filter((p) => p.features.includes(feature));
  if (!has.length) return null;
  if (has.length === sorted.length) return { text: 'All plans', all: true };
  const from = sorted.indexOf(has[0]!);
  if (sorted.slice(from).every((p) => p.features.includes(feature))) return { text: `${has[0]!.name} and up`, all: false };
  return { text: has.map((p) => p.name).join(', '), all: false };
}

/** One feature group on the Features page's overview (#776, #778): its features on the front, and on the back (turned
 *  over by "See how") what each one does and which plans it's in (#791). */
function OverviewCard({
  group: g,
  promises,
  plans,
  planFeatures,
}: {
  group: (typeof GUIDE)[number];
  promises?: { title: string; text: string }[];
  plans: PublicPlan[];
  planFeatures: string[];
}) {
  const [back, setBack] = useState(false);
  const tag = (feature?: string) => {
    const t = planTag(feature, plans, planFeatures);
    return t ? <span className={`landing-plan-tag ${t.all ? 'all' : ''}`}>{t.text}</span> : null;
  };
  return (
    // The group's id is its link target (/features#state, the home page's strip).
    <div id={g.id} className={`landing-flip ${back ? 'flipped' : ''}`}>
      <div className="card landing-overview-card landing-flip-front" aria-hidden={back}>
        <span className="landing-kicker">{g.name}</span>
        <h2>{g.title}</h2>
        <p className="muted landing-overview-intro">{g.intro}</p>
        <ul>
          {g.items.map((f) => (
            <li key={f.name}>{f.name}</li>
          ))}
        </ul>
        <button type="button" className="landing-link-more" onClick={() => setBack(true)} tabIndex={back ? -1 : 0}>
          See how <ChevronRight size={16} />
        </button>
      </div>
      <div className="card landing-overview-card landing-flip-back" aria-hidden={!back}>
        <span className="landing-kicker">{g.name}</span>
        <dl>
          {g.items.map((f) => (
            <div key={f.name}>
              <dt>
                {f.name} {tag(f.feature)}
              </dt>
              <dd>{f.text}</dd>
            </div>
          ))}
        </dl>
        {/* What used to be the Safety page (#780): the promises that answer "is this spyware?", on the back only (#795). */}
        {promises && (
          <div className="landing-promises">
            <span className="landing-kicker">Our promises</span>
            <dl>
              {promises.map((p) => (
                <div key={p.title}>
                  <dt>{p.title}</dt>
                  <dd>{p.text}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}
        <span className="landing-flip-actions">
          <button type="button" className="landing-link-more" onClick={() => setBack(false)} tabIndex={back ? 0 : -1}>
            <ChevronLeft size={16} /> Back
          </button>
        </span>
      </div>
    </div>
  );
}

function PageHead({ title, text }: { title: string; text: string }) {
  return (
    <section className="landing-page-head">
      <h1>{title}</h1>
      <p className="landing-lead">{text}</p>
    </section>
  );
}

/**
 * The public site for homeschooling families (#261, rebuilt #538), always in the light Ocean Mist theme (#698). Home is
 * short and links to its own pages for the features, the state paperwork, safety and privacy, and the FAQ (#702), all
 * with the same top menu and footer. Signed-in parents go straight to their dashboard from Home.
 */
export function Landing({ page = 'home' }: { page?: MarketingPage }) {
  useCustomCss('landing'); // an admin's custom styles (#367)
  const { registrationOpen } = useAuthOptions();
  const [contact, setContact] = useState<SupportContact | null>(null);
  useSiteTheme(); // light and airy (#698)
  // A friend's link (#554) or a partner's (#598): remembered before the offer is looked up.
  const [remembered] = useState(() => (rememberReferral(), true));
  const offer = usePartnerOffer();
  const open = registrationOpen || (remembered && !!offer?.signupsOpen);
  useEffect(() => {
    api
      .get<{ contact?: SupportContact | null }>('/api/auth/options')
      .then((o) => setContact(o.contact ?? null))
      .catch(() => undefined);
  }, []);
  const [plans, setPlans] = useState<{ plans: PublicPlan[]; largeFamily: boolean; catalog: { key: string; label: string }[] }>({ plans: [], largeFamily: false, catalog: [] });
  useEffect(() => {
    api
      .get<{ plans: PublicPlan[]; largeFamily: boolean; catalog: { key: string; label: string }[] }>('/api/plans')
      .then(setPlans)
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    const p = PAGES.find((x) => x.id === page);
    document.title = p ? `${p.title} · ClassGuardian` : 'ClassGuardian';
    // A link to one part of a page (/features#focus) opens there; anything else starts at the top.
    const target = window.location.hash && document.getElementById(window.location.hash.slice(1));
    if (target) target.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [page]);
  // The overview cards' fronts all as tall as the tallest (#797), measured with none turned over; a turned card still grows.
  const overview = useRef<HTMLElement>(null);
  useEffect(() => {
    const nav = overview.current;
    if (!nav) return;
    const fit = () => {
      nav.style.setProperty('--front-h', 'auto');
      const fronts = [...nav.querySelectorAll<HTMLElement>('.landing-flip:not(.flipped) > .landing-flip-front')];
      if (fronts.length) nav.style.setProperty('--front-h', `${Math.max(...fronts.map((f) => f.offsetHeight))}px`);
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [page, plans]);
  const start = open ? 'Start free' : 'Request access';
  const trial = trialText(plans.plans);
  const cheapest = plans.plans.length ? Math.min(...plans.plans.map((p) => p.monthlyCents)) : 0;
  const costAnswer = cheapest
    ? `Plans start at ${dollars(cheapest)} a month${trial ? `, and ${trial === 'Free trial' ? 'you can start with a free trial' : `every plan starts with a ${trial}`}` : ''}. See the plans on the home page for what each one includes.`
    : '';

  return (
    <div className="landing accents">
      <PartnerBanner offer={offer} />
      <header className="landing-nav">
        <Link to="/" className="logo-link" aria-label="ClassGuardian home">
          <Logo />
        </Link>
        <nav className="landing-menu" aria-label="Site">
          {PAGES.filter((p) => p.menu !== false).map((p) => (
            <Link key={p.id} to={p.path} className={`landing-nav-link ${page === p.id ? 'active' : ''}`} aria-current={page === p.id ? 'page' : undefined}>
              {p.label}
            </Link>
          ))}
        </nav>
        <span className="row-gap landing-nav-actions">
          <Link className="btn" to="/login">
            Sign in
          </Link>
          <Link className="btn primary" to="/signup">
            {open ? 'Get started' : 'Request access'}
          </Link>
        </span>
      </header>

      <main>
        {page === 'home' && (
          <>
            <section className="landing-hero wide">
              <div className="landing-hero-text">
                <p className="landing-eyebrow">
                  <Sparkles size={16} /> Built for homeschooling families{trial ? ` · ${trial}` : ''}
                </p>
                <h1>
                  Homeschool, organized.
                  <br />
                  <span className="landing-accent">School time, focused.</span>
                </h1>
                <p className="landing-lead">Plans, grades, attendance and your state’s paperwork in one place, and a computer that stays on task during school hours.</p>
                {/* Just the way into the features (#799); signing up waits for the plans and the closing card. */}
                <div className="landing-cta">
                  <Link className="landing-compare-link" to="/features">
                    See the features <ChevronRight size={16} />
                  </Link>
                </div>
                <ul className="landing-trust" aria-label="At a glance">
                  <li>
                    <Check size={16} /> Windows, Mac &amp; Chromebook
                  </li>
                  <li>
                    <Check size={16} /> Camera &amp; mic always show a banner
                  </li>
                  <li>
                    <Check size={16} /> Set up in about 10 minutes
                  </li>
                </ul>
              </div>
              <div className="landing-hero-visual" aria-hidden="true">
                <div className="landing-frame landing-hero-laptop">
                  <div className="landing-frame-bar">
                    <span />
                    <span />
                    <span />
                  </div>
                  <img src="/tour/family.jpg" width={1920} height={1200} alt="" />
                </div>
                <div className="landing-hero-phone">
                  <img src="/tour/phone.jpg" alt="" />
                </div>
              </div>
            </section>

            <nav className="card landing-strip" aria-label="What it does">
              {GUIDE.map((g) => (
                <Link key={g.id} to={`/features#${g.id}`}>
                  <strong>{g.name}</strong>
                  <span className="muted small">{g.intro}</span>
                  {/* Four highlights from the group (#803). */}
                  <ul className="landing-strip-points">
                    {g.highlights.map((h) => (
                      <li key={h}>{h}</li>
                    ))}
                  </ul>
                  <span className="landing-link-more">
                    See all {g.items.length} features <ChevronRight size={16} />
                  </span>
                </Link>
              ))}
            </nav>

            <PlansSection plans={plans.plans} largeFamily={plans.largeFamily} open={open} />
          </>
        )}

        {page === 'features' && (
          <>
            <section className="landing-page-head">
              <h1>Every feature, in one place</h1>
              <p className="landing-lead">
                Everything ClassGuardian does for your homeschool, grouped by what you’re trying to get done. <Link to="/demo">See it in action</Link>, or <Link to="/#plans">compare the plans</Link> to see what each one includes.
              </p>
            </section>
            {/* The problems first (#783), then what ClassGuardian does about them. */}
            <section className="landing-section">
              <h2 className="landing-section-title">Homeschooling is a lot to keep track of</h2>
              <div className="landing-pillars three">
                {PROBLEMS.map((p, i) => (
                  <article key={p.title} className="card landing-pillar landing-problem" style={{ ['--tile' as string]: `var(--coast-${i + 1})` }}>
                    <h3>{p.title}</h3>
                    <p className="muted">{p.text}</p>
                  </article>
                ))}
              </div>
            </section>
            {/* The four groups at a glance (#776); "See how" turns a card over to each feature's one-line description. */}
            <nav className="landing-overview" aria-label="Feature groups" ref={overview}>
              {GUIDE.map((g) => (
                <OverviewCard key={g.id} group={g} promises={g.id === 'focus' ? PROMISE : undefined} plans={plans.plans} planFeatures={plans.catalog.map((c) => c.key)} />
              ))}
            </nav>
            {plans.plans.some((p) => p.features.length) && (
              <p className="muted small landing-plan-legend">
                Turn a card over with See how to see which plans each feature is in, or <Link to="/compare-plans">compare the plans</Link>.
              </p>
            )}
            <section className="landing-section">
              <h2 className="landing-section-title">Made for the way you homeschool</h2>
              <div className="landing-fits">
                {FITS.map((f) => (
                  <div key={f.title} className="landing-fit">
                    <span className="landing-icon">{f.icon}</span>
                    <div>
                      <strong>{f.title}</strong>
                      <p className="muted small">{f.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
            <section id="how" className="landing-section">
              <h2 className="landing-section-title">Up and running in about 10 minutes</h2>
              <ol className="landing-steps">
                <li className="card">
                  <span className="landing-step-n">1</span>
                  <h3>Add your child</h3>
                  <p className="muted">Their name, grade and subjects. Add as many children as you have.</p>
                </li>
                <li className="card">
                  <span className="landing-step-n">2</span>
                  <h3>Install on their computer</h3>
                  <p className="muted">The ClassGuardian app on Windows or Mac, or the extension on a Chromebook. Pair it with a 6-digit code.</p>
                </li>
                <li className="card">
                  <span className="landing-step-n">3</span>
                  <h3>Set school hours</h3>
                  <p className="muted">Pick the learning sites, set the schedule and add your first assignments. That’s it.</p>
                </li>
              </ol>
            </section>
          </>
        )}

        {page === 'demo' && (
          <>
            <PageHead title="See it in action" text="A homeschool family’s week, with two kids." />
            <ProductTour bare />
          </>
        )}

        {page === 'ambassadors' && <AmbassadorsPage />}

        {page === 'compare' && <ComparePlans plans={plans.plans} largeFamily={plans.largeFamily} catalog={plans.catalog} open={open} />}

        {page === 'faq' && (
          <>
            <PageHead title="Questions parents ask" text="Can’t find yours? Call, text or email us below." />
            <section className="landing-section">
              <div className="landing-faq">
                {FAQ.map((f) => (
                  <details key={f.q} className="card">
                    <summary>{f.q}</summary>
                    <p className="muted">{f.q === 'What does it cost?' && costAnswer ? costAnswer : f.a}</p>
                  </details>
                ))}
              </div>
            </section>
          </>
        )}

        {/* Every page closes with this (#789): a light card, just above the footer. */}
        <section className="landing-final card">
          <p className="landing-final-line">
            Spend less time managing homeschool.
            <br />
            <span className="landing-accent">Spend more time enjoying it.</span>
          </p>
          {trial && <p className="muted">{trial === 'Free trial' ? 'Start with a free trial.' : `A ${trial} on every plan.`} No credit card required.</p>}
          <span className="landing-final-actions">
            <Link className="btn primary big" to="/signup">
              {start}
            </Link>
            {page !== 'demo' && (
              <Link className="btn big" to="/demo">
                See the demo
              </Link>
            )}
          </span>
          {contact && <ContactStrip contact={contact} />}
        </section>
      </main>

      <footer className="landing-footer muted small">
        {/* Logo, tagline and links share one row; "Powered by" sits on a line under the logo (#940). */}
        <div className="landing-footer-top">
          <Logo />
          <span>Homeschool, organized. School time, focused.</span>
          <span className="landing-footer-links">
            {PAGES.map((p) => (
              <Link key={p.id} to={p.path} className="landing-footer-link">
                {p.label}
              </Link>
            ))}
            {contact?.email && (
              <a href={`mailto:${contact.email}`} className="landing-footer-link">
                Contact
              </a>
            )}
            <Link to="/terms" className="landing-footer-link">
              Terms
            </Link>
            <Link to="/privacy" className="landing-footer-link">
              Privacy Policy
            </Link>
          </span>
        </div>
        <span className="landing-footer-by">Powered by Vieira Ventures LLC</span>
      </footer>
    </div>
  );
}
